// Phleb readiness — derived state between "finished onboarding wizard"
// and "assignable on the route planner."
//
// Drives:
//   - Readiness column + badge on the admin Phlebotomist Management list
//   - Readiness panel on the phleb detail page with Mark Done checkboxes
//   - Route Planner filter / warning (Phase 2)
//   - Phleb dashboard banner (Phase 2)
//
// Stages, earliest → latest:
//   in_onboarding         — phleb hasn't finished the wizard yet
//   awaiting_supplies     — onboarded, supplies not yet collected
//   supplies_picked_up    — supplies collected, not yet route-cleared
//   route_ready           — cleared for route assignment

const READINESS_CONFIG = {
  in_onboarding: {
    label: 'In Onboarding',
    color: 'gray',
    description: 'Phleb has not finished the onboarding wizard yet.',
  },
  awaiting_supplies: {
    label: 'Awaiting Supplies',
    color: 'yellow',
    description: 'Onboarded — waiting on the supply kit to be picked up or delivered.',
  },
  supplies_picked_up: {
    label: 'Supplies Picked Up',
    color: 'orange',
    description: 'Kit in hand — admin still needs to clear them for route assignment.',
  },
  route_ready: {
    label: 'Route-Ready',
    color: 'green',
    description: 'Cleared for route assignment. Eligible on the route planner.',
  },
};

const STAGE_ORDER = ['in_onboarding', 'awaiting_supplies', 'supplies_picked_up', 'route_ready'];

/**
 * Derive the readiness stage from a phlebotomist_profiles row.
 *
 * Uses fields:
 *   - onboarding_status              ('completed' marks the wizard done)
 *   - onboarding_completed_at
 *   - supplies_picked_up_at
 *   - route_ready_at
 *
 * Returns `{ code, label, color, description }`.
 */
export function getPhlebReadinessStage(phleb) {
  if (!phleb) return { code: 'in_onboarding', ...READINESS_CONFIG.in_onboarding };

  if (phleb.route_ready_at) {
    return { code: 'route_ready', ...READINESS_CONFIG.route_ready };
  }
  if (phleb.supplies_picked_up_at) {
    return { code: 'supplies_picked_up', ...READINESS_CONFIG.supplies_picked_up };
  }

  const onboardingDone =
    phleb.onboarding_status === 'completed' || !!phleb.onboarding_completed_at;
  if (onboardingDone) {
    return { code: 'awaiting_supplies', ...READINESS_CONFIG.awaiting_supplies };
  }
  return { code: 'in_onboarding', ...READINESS_CONFIG.in_onboarding };
}

export function getReadinessConfig(code) {
  return READINESS_CONFIG[code] || READINESS_CONFIG.in_onboarding;
}

export function getReadinessOrder() {
  return [...STAGE_ORDER];
}

/**
 * True when this phleb is eligible to be picked on the route planner.
 * Currently equivalent to "route_ready" but exported as its own helper so
 * we can change the criteria later (e.g. add a "license_expires_at" guard)
 * without touching the call sites.
 */
export function isRouteReady(phleb) {
  return !!phleb?.route_ready_at;
}
